import React, { useState, useRef, useEffect } from 'react';
import './FilledInput.css';
import './AddressInput.css';
import tooltipIcon from '../assets/tooltip.svg';

// Add Google Maps types
declare global {
  interface Window {
    google: {
      maps: {
        places: {
          AutocompleteService: {
            new(): {
              getPlacePredictions: (
                request: {
                  input: string;
                  types?: string[];
                  componentRestrictions?: { country: string };
                },
                callback: (
                  predictions: Array<{
                    description: string;
                    place_id: string;
                  }> | null,
                  status: string
                ) => void
              ) => void;
            };
          };
          PlacesService: {
            new(div: HTMLDivElement): {
              getDetails: (
                request: {
                  placeId: string;
                  fields: string[];
                },
                callback: (
                  place: {
                    address_components?: Array<{
                      long_name: string;
                      short_name: string;
                      types: string[];
                    }>;
                  } | null,
                  status: string
                ) => void
              ) => void;
            };
          };
          PlacesServiceStatus: {
            OK: string;
          };
          AutocompletePrediction: {
            description: string;
            place_id: string;
          };
          PlaceResult: {
            address_components?: Array<{
              long_name: string;
              short_name: string;
              types: string[];
            }>;
          };
        };
        GeocoderAddressComponent: {
          long_name: string;
          short_name: string;
          types: string[];
        };
      };
    };
  }
}

interface AddressComponents {
  streetNumber: string;
  streetName: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

interface AddressInputProps {
  id: string;
  label: string;
  placeholder?: string;
  errorMessage?: string;
  helperText?: string;
  validate?: (value: string) => string | null;
  inputChange?: (e: any) => void;
  tooltip?: string;
  onAddressComponentsChange?: (components: AddressComponents) => void;
}

interface AddressSuggestion {
  description: string;
  place_id: string;
}

const AddressInput: React.FC<AddressInputProps> = ({
  id,
  label,
  errorMessage,
  helperText,
  validate,
  tooltip,
  inputChange,
  onAddressComponentsChange,
}) => {
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const suggestionsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    // Load Google Places API script
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (inputChange) {
      inputChange(e);
    }
    const newValue = e.target.value;
    setValue(newValue);

    // If we have a Google Places service, get suggestions
    if (window.google && window.google.maps && window.google.maps.places) {
      const service = new window.google.maps.places.AutocompleteService();
      if (newValue.length > 2) {
        service.getPlacePredictions(
          {
            input: newValue,
            types: ['address'],
            componentRestrictions: { country: 'us' },
          },
          (predictions, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
              setSuggestions(predictions);
              setShowSuggestions(true);
            } else {
              setSuggestions([]);
              setShowSuggestions(false);
            }
          }
        );
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    }
  };

  const handleBlur = () => {
    if (validate && value.trim() !== '') {
      const error = validate(value);
      setError(error);
    }
    // Delay hiding suggestions to allow for clicking on them
    setTimeout(() => {
      setShowSuggestions(false);
    }, 200);
  };

  const handleSuggestionClick = (suggestion: AddressSuggestion) => {
    if (window.google && window.google.maps && window.google.maps.places) {
      const service = new window.google.maps.places.PlacesService(document.createElement('div'));
      service.getDetails(
        {
          placeId: suggestion.place_id,
          fields: ['address_components'],
        },
        (place, status) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && place && place.address_components) {
            // Extract all address components
            const components = {
              streetNumber: '',
              streetName: '',
              city: '',
              state: '',
              postalCode: '',
              country: '',
            };

            place.address_components.forEach((component) => {
              if (component.types.includes('street_number')) {
                components.streetNumber = component.long_name;
              } else if (component.types.includes('route')) {
                components.streetName = component.long_name;
              } else if (component.types.includes('locality')) {
                components.city = component.long_name;
              } else if (component.types.includes('administrative_area_level_1')) {
                components.state = component.short_name;
              } else if (component.types.includes('postal_code')) {
                components.postalCode = component.long_name;
              } else if (component.types.includes('country')) {
                components.country = component.short_name;
              }
            });

            const streetAddress = `${components.streetNumber} ${components.streetName}`.trim();

            // Update the input value
            setValue(streetAddress);
            setShowSuggestions(false);

            // Create a synthetic event to trigger inputChange
            if (inputChange) {
              const syntheticEvent = {
                target: {
                  id,
                  value: streetAddress,
                },
              };
              inputChange(syntheticEvent);
            }

            // Validate the address
            if (validate) {
              const error = validate(streetAddress);
              setError(error);
            }

            // Notify parent component about address components
            if (onAddressComponentsChange) {
              onAddressComponentsChange(components);
            }
          }
        }
      );
    }
  };

  return (
    <div className="input-wrapper address-input-wrapper">
      <input
        type="text"
        id={id}
        className="filled-input"
        placeholder=" "
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        ref={inputRef}
      />
      <label htmlFor={label} className="filled-label">
        {label}
      </label>

      {tooltip && (
        <div
          className="tooltip-icon"
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
        >
          <img src={tooltipIcon} alt="info" className="info-icon" />
          {tooltip && showTooltip && <div className="tooltip-message">{tooltip}</div>}
        </div>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div 
          className="address-suggestions" 
          ref={suggestionsRef}
          onMouseDown={(e) => e.preventDefault()} // Prevent input blur on suggestion click
        >
          {suggestions.map((suggestion) => (
            <div
              key={suggestion.place_id}
              className="suggestion-item"
              onMouseDown={() => handleSuggestionClick(suggestion)} // Use onMouseDown instead of onClick
            >
              {suggestion.description}
            </div>
          ))}
        </div>
      )}

      {(error || errorMessage) && <p className="filled-error-text">{error || errorMessage}</p>}
      {!error && !errorMessage && helperText && <p className="filled-helper-text">{helperText}</p>}
    </div>
  );
};

export default AddressInput; 